import { useWallet } from '@solana/wallet-adapter-react';
import { CircleAlert, Cloud, Info, ShieldCheck, Wallet } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { usePreferences } from '../../../contexts/PreferencesContext';
import { getErrorMessage } from '../../../utils/errorHandling';
import { captureAndReportError } from '../../../utils/errorReporting';
import {
  clearS3Config,
  decryptS3Config,
  encryptS3Config,
  getS3PublicConfig,
  hasS3Config,
  maskS3Config,
} from '../../../utils/s3CredentialManager';
import { S3Provider, type S3Config } from '../../../utils/upload-providers';
import { createWalletAdapter } from '../../../utils/wallet';
import { Button } from '../../Button';
import * as Form from '../../Form';
import { Input } from '../../Input';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { LoadingSpinner } from '../../ui/LoadingSpinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import type { ProviderConfigSectionProps } from './types';

const CLOUDFRONT_DOMAIN_REGEX =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/;

export const S3ConfigSchema = z
  .object({
    accessKeyId: z.string().min(1, 'Access Key ID is required'),
    secretAccessKey: z.string().min(1, 'Secret Access Key is required'),
    bucketName: z.string().min(1, 'Bucket Name is required'),
    region: z.string().min(1, 'Region is required'),
    pathPrefix: z.string(),
    useCloudFront: z.boolean(),
    cloudFrontDomain: z.string(),
  })
  .superRefine((values, context) => {
    if (!values.useCloudFront) return;

    if (!values.cloudFrontDomain) {
      context.addIssue({
        code: 'custom',
        path: ['cloudFrontDomain'],
        message: 'CloudFront domain is required when CloudFront is enabled',
      });
    } else if (!CLOUDFRONT_DOMAIN_REGEX.test(values.cloudFrontDomain)) {
      context.addIssue({
        code: 'custom',
        path: ['cloudFrontDomain'],
        message: 'Invalid CloudFront domain format',
      });
    }
  });

type S3FormValues = z.infer<typeof S3ConfigSchema>;
type S3TextFieldName = Exclude<keyof S3FormValues, 'useCloudFront'>;
type S3FieldErrors = Partial<Record<S3TextFieldName, string>>;

const DEFAULT_S3_FORM_VALUES: S3FormValues = {
  accessKeyId: '',
  secretAccessKey: '',
  bucketName: '',
  region: 'us-east-1',
  pathPrefix: '',
  useCloudFront: false,
  cloudFrontDomain: '',
};

function getS3FieldErrors(values: S3FormValues): S3FieldErrors {
  const result = S3ConfigSchema.safeParse(values);
  if (result.success) return {};

  const errors: S3FieldErrors = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0] as S3TextFieldName;
    errors[field] ??= issue.message;
  }
  return errors;
}

interface S3TextFieldProps {
  name: S3TextFieldName;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  disabled: boolean;
  error?: string;
  description?: string;
  required?: boolean;
  type?: 'text' | 'password';
}

function S3TextField({
  name,
  label,
  value,
  onChange,
  placeholder,
  disabled,
  error,
  description,
  required,
  type = 'text',
}: S3TextFieldProps) {
  const inputId = `s3-${name}`;
  const descriptionId = description ? `${inputId}-description` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={inputId} className="block">
        <Form.FieldLabel label={label} required={required} />
      </label>
      <Input
        id={inputId}
        name={name}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={label}
        aria-required={required}
        aria-invalid={!!error}
        aria-describedby={
          [descriptionId, errorId].filter(Boolean).join(' ') || undefined
        }
      />
      {description && (
        <p id={descriptionId} className="text-xs leading-4 text-seeker-400">
          {description}
        </p>
      )}
      {error && (
        <Form.FieldFeedback className="justify-start">
          <CircleAlert aria-hidden="true" />
          <span id={errorId}>{error}</span>
        </Form.FieldFeedback>
      )}
    </div>
  );
}

function InfoTip({
  icon,
  tip,
  className,
}: {
  icon: ReactNode;
  tip: string;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`mt-0.5 flex size-4 shrink-0 cursor-help items-center justify-center [&>svg]:size-4 ${className ?? ''}`}
        >
          {icon}
        </span>
      </TooltipTrigger>
      <TooltipContent className="border-seeker-700 bg-seeker-800">
        <p>{tip}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export const S3ConfigSection = ({
  onConfigUpdate,
}: ProviderConfigSectionProps) => {
  const wallet = useWallet();
  const { updatePreferences } = usePreferences();
  const [showS3ConfigModal, setShowS3ConfigModal] = useState(false);
  const [s3ConfigErrors, setS3ConfigErrors] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<S3FieldErrors>({});
  const [hasValidated, setHasValidated] = useState(false);
  const [testingS3, setTestingS3] = useState(false);
  const [hasS3Configuration, setHasS3Configuration] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [currentS3Config, setCurrentS3Config] =
    useState<Partial<S3Config> | null>(null);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [formValues, setFormValues] = useState(DEFAULT_S3_FORM_VALUES);
  const [initialFormValues, setInitialFormValues] = useState(
    DEFAULT_S3_FORM_VALUES,
  );
  const isDirty =
    JSON.stringify(formValues) !== JSON.stringify(initialFormValues);

  const resetForm = (values = DEFAULT_S3_FORM_VALUES) => {
    setFormValues(values);
    setInitialFormValues(values);
    setFieldErrors({});
    setHasValidated(false);
    setS3ConfigErrors([]);
  };

  const updateFormValue = (name: S3TextFieldName, value: string) => {
    const nextValues = { ...formValues, [name]: value };
    setFormValues(nextValues);
    if (hasValidated) setFieldErrors(getS3FieldErrors(nextValues));
  };

  useEffect(() => {
    const checkS3Config = async () => {
      try {
        const configured = hasS3Config();
        setHasS3Configuration(configured);

        const publicConfig = getS3PublicConfig();
        if (publicConfig) {
          setCurrentS3Config({
            accessKeyId: '****',
            secretAccessKey: '****',
            bucketName: publicConfig.bucketName,
            region: publicConfig.region,
            pathPrefix: publicConfig.pathPrefix,
            cloudFrontDomain: publicConfig.cloudFrontDomain,
          });
        } else if (configured) {
          const walletAdapter = createWalletAdapter(wallet, {
            throwOnInvalid: false,
            includeSendTransaction: false,
          });
          if (walletAdapter) {
            const config = await decryptS3Config(walletAdapter);
            if (config) setCurrentS3Config(maskS3Config(config));
          }
        } else {
          setCurrentS3Config(null);
        }
      } catch (error) {
        captureAndReportError('Failed to check S3 configuration:', error);
      }
    };

    void checkS3Config();
  }, [wallet.publicKey, wallet.signMessage]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setHasValidated(true);

    const result = S3ConfigSchema.safeParse(formValues);
    if (!result.success) {
      setFieldErrors(getS3FieldErrors(formValues));
      return;
    }

    setTestingS3(true);
    setS3ConfigErrors([]);
    setFieldErrors({});

    const { useCloudFront, cloudFrontDomain, ...config } = result.data;
    const configToSave: S3Config = {
      ...config,
      cloudFrontDomain: useCloudFront ? cloudFrontDomain : undefined,
    };

    try {
      const provider = new S3Provider();
      const walletAdapter = createWalletAdapter(wallet, {
        throwOnInvalid: false,
        includeSendTransaction: false,
      });

      await encryptS3Config(configToSave, walletAdapter);
      await provider.initialize({ walletAdapter: walletAdapter || undefined });
      await provider.testConfiguration();

      setHasS3Configuration(true);
      setCurrentS3Config(maskS3Config(configToSave));
      setShowS3ConfigModal(false);
      resetForm();
      onConfigUpdate?.();

      try {
        await updatePreferences({ uploadProvider: 's3' });
        toast.success('S3 configured and set as your default upload provider!');
      } catch (error) {
        captureAndReportError('Failed to save S3 as default provider:', error);
        toast.error(
          'S3 configured but failed to save as default provider. Please save manually.',
        );
      }
    } catch (error) {
      captureAndReportError('S3 configuration test failed:', error);

      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        setS3ConfigErrors([
          'CORS configuration error. Please ensure your S3 bucket has proper CORS settings. ' +
            'See the documentation for detailed CORS setup instructions.',
        ]);
      } else if (
        error instanceof Error &&
        error.message.includes('AccessControlListNotSupported')
      ) {
        setS3ConfigErrors([
          'Your S3 bucket has ACLs disabled (recommended). To make objects publicly accessible, ' +
            'add a bucket policy in S3 → Permissions → Bucket Policy. See the configuration ' +
            'instructions below for the required policy.',
        ]);
      } else {
        setS3ConfigErrors([getErrorMessage(error)]);
      }
    } finally {
      setTestingS3(false);
    }
  };

  const handleClearS3Config = () => {
    setShowClearDialog(true);
  };

  const confirmClearS3Config = () => {
    clearS3Config();
    setHasS3Configuration(false);
    setCurrentS3Config(null);
    setShowClearDialog(false);
    toast.success('S3 configuration cleared');
    onConfigUpdate?.();
  };

  const handleDialogOpen = async () => {
    setShowS3ConfigModal(true);
    resetForm({
      ...DEFAULT_S3_FORM_VALUES,
      useCloudFront: !!currentS3Config?.cloudFrontDomain,
    });

    if (!hasS3Configuration || !currentS3Config) return;

    try {
      const walletAdapter = createWalletAdapter(wallet, {
        throwOnInvalid: false,
        includeSendTransaction: false,
      });
      if (!walletAdapter) return;

      const fullConfig = await decryptS3Config(walletAdapter);
      if (fullConfig) {
        resetForm({
          accessKeyId: fullConfig.accessKeyId,
          secretAccessKey: fullConfig.secretAccessKey,
          bucketName: fullConfig.bucketName,
          region: fullConfig.region || 'us-east-1',
          pathPrefix: fullConfig.pathPrefix || '',
          useCloudFront: !!fullConfig.cloudFrontDomain,
          cloudFrontDomain: fullConfig.cloudFrontDomain || '',
        });
      }
    } catch (error) {
      captureAndReportError('Failed to load full S3 configuration:', error);
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open && isDirty && !testingS3) {
      setShowUnsavedChangesDialog(true);
      return;
    }

    setShowS3ConfigModal(open);
    if (!open) resetForm(initialFormValues);
  };

  const handleDiscardChanges = () => {
    setShowUnsavedChangesDialog(false);
    setShowS3ConfigModal(false);
    resetForm(initialFormValues);
  };

  const handleKeepEditing = () => setShowUnsavedChangesDialog(false);

  const handleUseCloudFrontChange = (checked: boolean) => {
    const nextValues = {
      ...formValues,
      useCloudFront: checked === true,
      cloudFrontDomain: checked === true ? formValues.cloudFrontDomain : '',
    };
    setFormValues(nextValues);
    if (hasValidated) setFieldErrors(getS3FieldErrors(nextValues));
  };

  return (
    <TooltipProvider>
      <div className="mt-4 border-t border-seeker-700 pt-4">
        <div className="flex flex-col gap-3">
          <h4 className="text-sm font-medium text-white">Configuration</h4>

          {hasS3Configuration && currentS3Config ? (
            <div className="flex flex-col gap-3 rounded-lg bg-seeker-900 p-4">
              <div className="flex items-center gap-2">
                <InfoTip
                  icon={<ShieldCheck className="text-white" />}
                  tip="Credentials are encrypted and stored locally"
                  className="mt-0"
                />
                <span className="text-xs font-medium leading-4 text-white">
                  Current Configuration
                </span>
                {currentS3Config.cloudFrontDomain && (
                  <InfoTip
                    icon={<Cloud className="text-green-400" />}
                    tip="CloudFront CDN enabled"
                    className="mt-0"
                  />
                )}
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div className="min-w-0 space-y-1 font-mono text-xs leading-4 text-seeker-400">
                  <p className="truncate">
                    Bucket:{' '}
                    <span className="text-white">
                      {currentS3Config.bucketName}
                    </span>
                  </p>
                  <p className="truncate">
                    Region:{' '}
                    <span className="text-white">
                      {currentS3Config.region || 'us-east-1'}
                    </span>
                  </p>
                  {currentS3Config.pathPrefix && (
                    <p className="truncate">
                      Path:{' '}
                      <span className="text-white">
                        {currentS3Config.pathPrefix}
                      </span>
                    </p>
                  )}
                  {currentS3Config.cloudFrontDomain && (
                    <p className="truncate">
                      CloudFront:{' '}
                      <span className="text-white">
                        {currentS3Config.cloudFrontDomain}
                      </span>
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="dangerOutline"
                    className="h-8 px-3"
                    onClick={handleClearS3Config}
                  >
                    <span className="text-xs">Clear</span>
                  </Button>
                  <Button
                    variant="outlineMuted"
                    className="h-8 px-3"
                    onClick={handleDialogOpen}
                  >
                    <span className="text-xs">Update</span>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-xs leading-4 text-seeker-400">
                No configuration found. Set up your S3 credentials to start
                uploading.
              </p>
              <Button
                variant="outlineWhite"
                className="w-full sm:w-auto sm:self-end"
                onClick={handleDialogOpen}
              >
                Configure S3
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showS3ConfigModal} onOpenChange={handleDialogClose}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-xl border-seeker-800 bg-seeker-900 p-6 sm:max-w-[700px]">
          <form onSubmit={onSubmit}>
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-white">
                Configure AWS S3
              </DialogTitle>
              <DialogDescription className="text-sm text-seeker-400">
                Enter your AWS S3 credentials and bucket information. These
                credentials will be encrypted and stored locally in your
                browser. They are never sent to our servers.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="flex flex-col gap-6">
              <div className="flex items-start gap-3 rounded-lg border border-seeker-700 bg-seeker-800 p-4">
                <InfoTip
                  icon={<Wallet className="text-seeker-400" />}
                  tip="Wallet is used for credential encryption"
                />
                <div className="min-w-0 flex-1">
                  <p className="mb-1 text-sm font-medium text-white">
                    {wallet.publicKey ? 'Wallet Connected' : 'Wallet Required'}
                  </p>
                  {wallet.publicKey ? (
                    <p className="text-xs leading-4 text-seeker-400">
                      Credentials will be encrypted with wallet:
                      <span className="mt-1 block break-all font-mono text-white">
                        {wallet.publicKey.toBase58()}
                      </span>
                    </p>
                  ) : (
                    <p className="text-xs leading-4 text-amber-400">
                      Please connect your wallet to save credentials. The
                      credentials will be encrypted using your wallet's public
                      key.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-lg border border-amber-800/30 bg-amber-950/30 p-4">
                <InfoTip
                  icon={<Info className="text-amber-400" />}
                  tip="Best practices for AWS IAM configuration"
                />
                <div className="text-sm text-amber-300">
                  <p className="mb-1 font-medium">Security Recommendations:</p>
                  <ul className="list-inside list-disc space-y-1 text-xs leading-4 text-amber-200">
                    <li>
                      Create a dedicated IAM user with minimal permissions
                    </li>
                    <li>
                      Only grant s3:PutObject and s3:PutObjectAcl permissions
                    </li>
                    <li>Restrict access to a specific bucket</li>
                    <li>Enable bucket versioning for data protection</li>
                  </ul>
                </div>
              </div>

              {s3ConfigErrors.length > 0 && (
                <div className="rounded-lg border border-red-800/30 bg-red-950/30 p-4">
                  <p className="mb-2 text-sm font-medium text-red-400">
                    Please fix the following errors:
                  </p>
                  <ul className="list-inside list-disc space-y-1">
                    {s3ConfigErrors.map((error, index) => (
                      <li
                        key={`${error}-${index}`}
                        className="text-xs leading-4 text-red-300"
                      >
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-col gap-4">
                <S3TextField
                  name="accessKeyId"
                  label="AWS Access Key ID"
                  value={formValues.accessKeyId}
                  onChange={(value) => updateFormValue('accessKeyId', value)}
                  placeholder="AKIAIOSFODNN7EXAMPLE"
                  disabled={testingS3}
                  error={fieldErrors.accessKeyId}
                  required
                />

                <S3TextField
                  name="secretAccessKey"
                  label="AWS Secret Access Key"
                  value={formValues.secretAccessKey}
                  onChange={(value) =>
                    updateFormValue('secretAccessKey', value)
                  }
                  placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                  disabled={testingS3}
                  error={fieldErrors.secretAccessKey}
                  type="password"
                  required
                />

                <S3TextField
                  name="bucketName"
                  label="S3 Bucket Name"
                  value={formValues.bucketName}
                  onChange={(value) => updateFormValue('bucketName', value)}
                  placeholder="my-nft-uploads"
                  disabled={testingS3}
                  error={fieldErrors.bucketName}
                  required
                />

                <S3TextField
                  name="region"
                  label="AWS Region"
                  value={formValues.region}
                  onChange={(value) => updateFormValue('region', value)}
                  placeholder="us-east-1"
                  disabled={testingS3}
                  error={fieldErrors.region}
                  description="Default: us-east-1. Use the region where your bucket is located."
                />

                <S3TextField
                  name="pathPrefix"
                  label="Path Prefix (Optional)"
                  value={formValues.pathPrefix}
                  onChange={(value) => updateFormValue('pathPrefix', value)}
                  placeholder="nft-metadata/uploads"
                  disabled={testingS3}
                  error={fieldErrors.pathPrefix}
                  description="Optional folder path within your bucket for organizing uploads."
                />

                <div className="border-t border-seeker-800 pt-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="use-cloudfront"
                      aria-label="Use CloudFront CDN"
                      checked={formValues.useCloudFront}
                      onChange={(event) =>
                        handleUseCloudFrontChange(event.currentTarget.checked)
                      }
                      disabled={testingS3}
                      className="size-4 rounded border-seeker-700 accent-white disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <label
                      htmlFor="use-cloudfront"
                      className="cursor-pointer text-sm font-bold text-white"
                    >
                      Use CloudFront Distribution
                    </label>
                  </div>
                  <p className="ml-7 mt-1 text-xs leading-4 text-seeker-400">
                    Enable CDN delivery through Amazon CloudFront for improved
                    global performance
                  </p>
                </div>

                {/* 0fr→1fr grid row animates to the real height (no max-h guess);
                    `inert` keeps the hidden fields out of the tab order. */}
                <div
                  inert={!formValues.useCloudFront}
                  className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
                    formValues.useCloudFront
                      ? 'grid-rows-[1fr] opacity-100'
                      : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="flex min-h-0 flex-col gap-4 overflow-hidden">
                    <S3TextField
                      name="cloudFrontDomain"
                      label="CloudFront Domain"
                      value={formValues.cloudFrontDomain}
                      onChange={(value) =>
                        updateFormValue('cloudFrontDomain', value)
                      }
                      placeholder="d1234567890.cloudfront.net or cdn.yourdomain.com"
                      disabled={testingS3}
                      error={fieldErrors.cloudFrontDomain}
                      description="Your CloudFront distribution domain for CDN delivery"
                      required
                    />

                    <div className="flex items-start gap-3 rounded-lg border border-seeker-700 bg-seeker-800 p-4">
                      <InfoTip
                        icon={<Info className="text-seeker-400" />}
                        tip="Step-by-step CloudFront setup guide"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="mb-2 text-sm font-medium text-white">
                          CloudFront Setup Instructions
                        </p>
                        <ol className="list-inside list-decimal space-y-2 text-xs leading-4 text-seeker-300">
                          <li>
                            <strong>Create Distribution:</strong> In AWS Console
                            → CloudFront → Create Distribution
                          </li>
                          <li>
                            <strong>Origin Settings:</strong>
                            <div className="ml-4 mt-1 break-all font-mono text-xs text-white">
                              Origin Domain:{' '}
                              {currentS3Config?.bucketName || 'your-bucket'}
                              .s3.{currentS3Config?.region || 'region'}
                              .amazonaws.com
                            </div>
                          </li>
                          <li>
                            <strong>Cache Behavior:</strong> Set Viewer Protocol
                            Policy to "Redirect HTTP to HTTPS"
                          </li>
                          <li>
                            <strong>CORS Headers:</strong> Create Response
                            Headers Policy with:
                            <div className="ml-4 mt-1 break-all rounded bg-seeker-950 p-2 font-mono text-xs text-white">
                              Access-Control-Allow-Origin:
                              https://publish.solanamobile.com
                            </div>
                          </li>
                          <li>
                            <strong>Wait for Deployment:</strong> Takes 15-20
                            minutes to deploy globally
                          </li>
                          <li>
                            <strong>Copy Domain:</strong> Use the CloudFront
                            domain (d1234567890.cloudfront.net) above
                          </li>
                        </ol>
                        <p className="mt-3 text-xs leading-4 text-seeker-400">
                          <strong>Note:</strong> Ensure your S3 bucket has
                          public read access configured
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3 rounded-lg border border-orange-800/30 bg-orange-950/30 p-4">
                  <InfoTip
                    icon={<Info className="text-orange-400" />}
                    tip="Required for browser-based uploads"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="mb-2 text-sm font-medium text-orange-400">
                      Required S3 Bucket CORS Configuration
                    </p>
                    <p className="mb-3 text-xs leading-4 text-orange-300">
                      Without proper CORS configuration, uploads will fail with
                      a "blocked by CORS policy" error.
                    </p>
                    <div className="whitespace-pre-wrap break-all rounded bg-seeker-950 p-3 font-mono text-xs text-seeker-300">
                      {`[
  {
    "AllowedHeaders": ["*"],
    "AllowedMethods": [
      "GET", "PUT", "POST", 
      "DELETE", "HEAD"
    ],
    "AllowedOrigins": [
      "https://publish.solanamobile.com"
    ],
    "ExposeHeaders": [
      "ETag", 
      "x-amz-version-id"
    ],
    "MaxAgeSeconds": 3000
  }
]`}
                    </div>
                    <p className="mt-2 text-xs leading-4 text-orange-300">
                      Add this in S3 Console → Permissions → CORS → Edit
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 rounded-lg border border-blue-800/30 bg-blue-950/30 p-4">
                  <InfoTip
                    icon={<Info className="text-blue-400" />}
                    tip="Required for NFT metadata accessibility"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="mb-2 text-sm font-medium text-blue-400">
                      Required S3 Bucket Policy for Public Access
                    </p>
                    <p className="mb-3 text-xs leading-4 text-blue-300">
                      NFT metadata must be publicly accessible. Add this bucket
                      policy to allow public read access while keeping ACLs
                      disabled (recommended).
                    </p>
                    <div className="whitespace-pre-wrap break-all rounded bg-seeker-950 p-3 font-mono text-xs text-seeker-300">
                      {`{
  "Version": "2012-10-17",
  "Statement": [
    {
      "Sid": "PublicReadGetObject",
      "Effect": "Allow",
      "Principal": "*",
      "Action": "s3:GetObject",
      "Resource": "arn:aws:s3:::YOUR_BUCKET_NAME/*"
    }
  ]
}`}
                    </div>
                    <p className="mt-2 text-xs leading-4 text-blue-300">
                      Replace YOUR_BUCKET_NAME with your actual bucket name. Add
                      this in S3 Console → Permissions → Bucket Policy → Edit
                    </p>
                    <p className="mt-2 text-xs leading-4 text-seeker-400">
                      Note: Keep "Block all public access" disabled for the
                      bucket, but leave "Bucket owner enforced" enabled for
                      Object Ownership.
                    </p>
                  </div>
                </div>
              </div>
            </DialogBody>
            <DialogFooter className="gap-2">
              <Button
                variant="outlineMuted"
                className="w-full sm:w-[120px]"
                onClick={() => handleDialogClose(false)}
                disabled={testingS3}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                className="w-full sm:min-w-[140px] sm:w-auto"
                disabled={testingS3 || !wallet.publicKey}
              >
                {testingS3 ? (
                  <>
                    <LoadingSpinner size="sm" label="Testing" />
                    Testing…
                  </>
                ) : !wallet.publicKey ? (
                  'Connect Wallet to Save'
                ) : (
                  'Save & Test Configuration'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showUnsavedChangesDialog}
        onOpenChange={setShowUnsavedChangesDialog}
      >
        <DialogContent className="max-w-md rounded-xl border-seeker-800 bg-seeker-900 p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white">
              Unsaved Changes
            </DialogTitle>
            <DialogDescription className="text-sm text-seeker-400">
              You have unsaved changes in the S3 configuration. Are you sure you
              want to discard them?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outlineMuted"
              className="w-full sm:w-auto"
              onClick={handleKeepEditing}
            >
              Keep Editing
            </Button>
            <Button
              variant="dangerOutline"
              className="w-full sm:w-auto"
              onClick={handleDiscardChanges}
            >
              Discard Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent className="max-w-md rounded-xl border-seeker-800 bg-seeker-900 p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white">
              Clear S3 configuration
            </DialogTitle>
            <DialogDescription className="text-sm text-seeker-400">
              Your saved bucket credentials will be removed from this browser.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outlineMuted"
              className="w-full sm:w-auto"
              onClick={() => setShowClearDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="dangerOutline"
              className="w-full sm:w-auto"
              onClick={confirmClearS3Config}
            >
              Clear configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};
